import { clsx } from 'clsx';
import { FC, PropsWithChildren, useId } from 'react';
import { SwiperProps } from 'swiper/react';
import { NavigationOptions } from 'swiper/types';
import { Carousel } from 'atoms/Carousel';
import { TABLET_WIDTH } from 'modules/mui/hooks';
import { NavigationButtons, NavigationTypes } from './NavigationButtons';
import styles from './slider.module.css';

export interface SliderProps extends SwiperProps {
  buttonContainerClass?: string;
  buttonPrevClass?: string;
  buttonNextClass?: string;
  sliderClassName?: string;
  navigationType?: NavigationTypes;
}

export const Slider: FC<PropsWithChildren<SliderProps>> = ({
  className,
  children,
  buttonPrevClass = '',
  buttonNextClass = '',
  buttonContainerClass,
  navigation,
  pagination,
  wrapperClass,
  navigationType = NavigationTypes.DEFAULT,
  breakpoints = {},
  ...restSliderProps
}) => {
  const getClearValue = (str: string): string =>
    str.replace(/[^a-zA-Z0-9]/g, '');
  const prevDynamicClass = getClearValue(useId());
  const nextDynamicClass = getClearValue(useId());
  const nav = (
    typeof navigation === 'boolean' ? { enabled: navigation } : navigation || {}
  ) as NavigationOptions;
  const {
    [TABLET_WIDTH]: tabletBreakpointConfig = {},
    ...breakpointsWithoutTablet
  } = breakpoints;
  const breakpointsConfig = {
    [TABLET_WIDTH]: {
      navigation: { enabled: navigationType === NavigationTypes.COMPACT },
      ...tabletBreakpointConfig,
    },
    ...breakpointsWithoutTablet,
  };

  return (
    <div className={clsx(styles.root, className)}>
      <Carousel
        navigation={{
          prevEl: `.${prevDynamicClass}`,
          nextEl: `.${nextDynamicClass}`,
          disabledClass: styles.buttonDisabled,
          lockClass: styles.lock,
          ...nav,
        }}
        pagination={{
          horizontalClass: styles.paginationContainer,
          lockClass: styles.lock,
          ...(typeof pagination !== 'boolean' ? pagination : {}),
        }}
        wrapperClass={clsx(
          styles[`navigationWrapper-${navigationType}`],
          wrapperClass,
        )}
        breakpoints={breakpointsConfig}
        {...restSliderProps}
      >
        {children}
      </Carousel>

      <NavigationButtons
        navigation={nav}
        breakpoints={breakpointsConfig}
        type={navigationType}
        classes={{
          container: buttonContainerClass,
          prev: `${prevDynamicClass} ${buttonPrevClass}`,
          next: `${nextDynamicClass} ${buttonNextClass}`,
        }}
      />
    </div>
  );
};
